import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagedQuery } from "./use-paged-query";
import { api } from "./api-client";
import { ACCOUNTS_KEY } from "./accounts";

export type ZaloGroup = {
    id: string;
    accountId: string;
    groupId: string;
    name: string;
    avatarUrl: string | null;
    memberCount: number;
    isAdmin: boolean;
    isSendLocked: boolean;
    leftAt: string | null;
    lastSyncedAt: string;
};

export type GroupSet = {
    id: string;
    accountId: string;
    name: string;
    description: string | null;
    groupIds: string[];
    sendableCount: number;
    createdAt: string;
};

const GROUPS_KEY = "groups";
const GROUP_SETS_KEY = "group-sets";

export function useGroups(accountId: string | undefined, search?: string) {
    const params = new URLSearchParams();
    if (accountId) params.set("accountId", accountId);
    if (search?.trim()) params.set("q", search.trim());

    return usePagedQuery<ZaloGroup>({
        queryKey: ["groups", accountId, search ?? ""],
        path: `/api/groups?${params.toString()}`,
        enabled: Boolean(accountId),
    });
}

export function useSyncGroups() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (accountId: string) =>
            api.post<{ total: number; created: number; updated: number; left: number }>(
                `/api/accounts/${accountId}/sync-groups`,
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] });
            void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
        },
    });
}

export function useGroupSets(accountId?: string) {
    return useQuery({
        queryKey: [GROUP_SETS_KEY, accountId ?? null],
        queryFn: () => api.get<GroupSet[]>(`/api/group-sets${accountId ? `?accountId=${accountId}` : ""}`),
        enabled: Boolean(accountId),
    });
}

function useGroupSetMutation<TVariables>(fn: (variables: TVariables) => Promise<unknown>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [GROUP_SETS_KEY] }),
    });
}

export function useCreateGroupSet() {
    return useGroupSetMutation((body: { accountId: string; name: string; description?: string; groupIds: string[] }) =>
        api.post<GroupSet>("/api/group-sets", body),
    );
}

export function useUpdateGroupSet() {
    return useGroupSetMutation(({ id, ...body }: { id: string; name?: string; groupIds?: string[] }) =>
        api.patch<GroupSet>(`/api/group-sets/${id}`, body),
    );
}

export function useDeleteGroupSet() {
    return useGroupSetMutation((id: string) => api.delete(`/api/group-sets/${id}`));
}
